/**
 * Partner Offer Types
 * Types for managing partner offers and categories
 */

import type { BaseEntity, UUID } from "./common.types";

// Status enum
export enum PartnerOfferStatus {
  DRAFT = "draft",
  SCHEDULED = "scheduled",
  ACTIVE = "active",
  PAUSED = "paused",
  EXPIRED = "expired",
  ARCHIVED = "archived",
}

// Brand targeting enum
export enum PartnerOfferBrand {
  USLS_ONLY = "usls_only",
  EMERGENCY_SHIELD_ONLY = "emergency_shield_only",
  BOTH = "both",
}

// Offer type enum (insurance vs non-insurance states)
export enum PartnerOfferType {
  INSURANCE = "insurance",
  NON_INSURANCE = "non_insurance",
  BOTH = "both",
}

// Offer lifecycle classification (FR-04). Orthogonal to category_id (business
// theme). STANDING = evergreen, no dates; LIGHTNING = time-boxed, dates required.
export enum PartnerOfferKind {
  STANDING = "standing",
  LIGHTNING = "lightning",
}

// Event type enum
export enum PartnerOfferEventType {
  IMPRESSION = "impression",
  CLICK = "click",
  CLAIM = "claim",
  REDEEM = "redeem",
}

// =====================================================
// CATEGORY INTERFACES
// =====================================================

/**
 * Partner offer category
 */
export interface PartnerOfferCategory extends BaseEntity {
  parent_id?: UUID;
  name: string;
  slug: string;
  description?: string;
  is_active: boolean;
  display_order: number;
  children?: PartnerOfferCategory[];
}

// =====================================================
// OFFER INTERFACES
// =====================================================

/**
 * Source organization (simplified for joins)
 */
export interface SourceOrganization {
  id: UUID;
  name: string;
}

/**
 * Partner offer
 */
export interface PartnerOffer extends BaseEntity {
  external_id?: string;
  // FR-03: nullable — Source Organization is the identity; company_name optional.
  company_name?: string | null;
  company_logo_url?: string;
  headline: string;
  description?: string;
  short_description?: string;
  offer_amount: string;
  offer_link?: string;
  promo_code?: string;
  legal_disclaimer?: string;
  terms_and_conditions?: string;
  redemption_instructions?: string;
  image_url?: string;
  category_id?: UUID;
  category?: PartnerOfferCategory;
  source_organization_id?: UUID;
  source_organization?: SourceOrganization;
  tags: string[];
  // FR-04: Standing (evergreen) vs Lightning (time-boxed). Drives FR-05 dates.
  offer_kind: PartnerOfferKind;
  // FR-05: nullable — Standing offers have NULL dates (always active);
  // Lightning offers carry real dates.
  start_date?: string | null;
  end_date?: string | null;
  start_time: string;
  end_time: string;
  timezone: string;
  brand_target: PartnerOfferBrand;
  offer_type: PartnerOfferType;
  target_states?: string[];
  target_all_states: boolean;
  is_featured: boolean;
  priority_score: number;
  status: PartnerOfferStatus;
  max_redemptions?: number;
  redemptions_per_member: number;
  metadata?: Record<string, unknown>;
  total_impressions: number;
  total_claims: number;
  // External scoring integration
  external_score?: number;
  score_source?: string;
  score_updated_at?: string;
  effective_score?: number;
  created_by?: UUID;
  updated_by?: UUID;
}

/**
 * Partner offer event (tracking)
 */
export interface PartnerOfferEvent extends BaseEntity {
  offer_id: UUID;
  event_type: PartnerOfferEventType;
  member_id?: string;
  member_state?: string;
  source?: string;
  session_id?: string;
  occurred_at: string;
  metadata?: Record<string, unknown>;
}

/**
 * State insurance classification
 */
export interface StateInsuranceClassification {
  state_code: string;
  state_name: string;
  classification: "insurance" | "non_insurance";
  notes?: string;
  updated_at: string;
  updated_by?: UUID;
}

/**
 * Partner offer API key
 */
export interface PartnerOfferApiKey extends BaseEntity {
  name: string;
  key_prefix: string;
  can_create: boolean;
  can_read: boolean;
  can_update: boolean;
  can_delete: boolean;
  rate_limit_per_minute: number;
  rate_limit_per_day: number;
  is_active: boolean;
  last_used_at?: string;
  created_by?: UUID;
  revoked_at?: string;
  revoked_by?: UUID;
}

// =====================================================
// FORM TYPES
// =====================================================

/**
 * Create/update offer form data
 */
export interface PartnerOfferFormData {
  external_id?: string;
  // FR-03: optional — the Company Name box was removed; Source Org is the identity.
  company_name?: string | null;
  company_logo_url?: string;
  headline: string;
  description?: string;
  short_description?: string;
  offer_amount: string;
  offer_link?: string;
  promo_code?: string;
  legal_disclaimer?: string;
  terms_and_conditions?: string;
  redemption_instructions?: string;
  image_url?: string;
  category_id?: UUID;
  source_organization_id?: UUID;
  tags?: string[];
  // FR-04/FR-05: kind drives whether dates apply. Standing → dates omitted/null.
  offer_kind: PartnerOfferKind;
  start_date?: string | null;
  end_date?: string | null;
  start_time?: string;
  end_time?: string;
  timezone?: string;
  brand_target: PartnerOfferBrand;
  offer_type: PartnerOfferType;
  target_states?: string[];
  target_all_states: boolean;
  is_featured: boolean;
  priority_score: number;
  status: PartnerOfferStatus;
  max_redemptions?: number;
  redemptions_per_member?: number;
}

/**
 * Offer list filters
 */
export interface PartnerOfferFilters {
  status?: PartnerOfferStatus;
  category_id?: UUID;
  source_organization_id?: UUID;
  offer_kind?: PartnerOfferKind;
  brand_target?: PartnerOfferBrand;
  is_featured?: boolean;
  search?: string;
  tags?: string[];
}

/**
 * Sort options for offer list
 */
export type PartnerOfferSortField =
  | "created_at"
  | "start_date"
  | "end_date"
  | "company_name"
  | "priority_score"
  | "effective_score"
  | "total_impressions"
  | "total_claims";

export interface PartnerOfferSort {
  field: PartnerOfferSortField;
  ascending: boolean;
}

// =====================================================
// STATISTICS TYPES
// =====================================================

/**
 * Summary statistics for all offers
 */
export interface PartnerOfferStats {
  offers: {
    total: number;
    active: number;
    draft: number;
    scheduled: number;
    featured: number;
  };
  lifetime: {
    impressions: number;
    claims: number;
    conversionRate: string;
  };
  last7Days: {
    impressions: number;
    clicks: number;
    claims: number;
    redemptions: number;
  };
  topPerformers: {
    id: string;
    company_name?: string | null;
    headline: string;
    total_impressions: number;
    total_claims: number;
  }[];
}
